import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import LoginPageLayout from './components/layouts/LoginPageLayout.jsx';
import ContactManagementPageLayout from './components/layouts/ContactManagementPageLayout.jsx';
import RoleRoute from './components/RoleRoute.jsx';
import './App.scss';

// Access & Identity pages.
const LoginPage = lazy(() => import('./pages/LoginPage.jsx'));
const RegisterPage = lazy(() => import('./pages/RegisterPage.jsx'));
const DashboardPage = lazy(() => import('./pages/DashboardPage.jsx'));
const ContactsPage = lazy(() => import('./pages/ContactsPage.jsx'));
const SenderIdPage = lazy(() => import('./pages/SenderIdPage.jsx'));
const CreditsPage = lazy(() => import('./pages/CreditsPage.jsx'));
const MyCreditsPage = lazy(() => import('./pages/MyCreditsPage.jsx'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage.jsx'));
const ComposeSmsPage = lazy(() => import('./pages/ComposeSmsPage.jsx'));
const ProfilePage = lazy(() => import('./pages/ProfilePage.jsx'));
const GroupSmsPage = lazy(() => import('./pages/GroupSmsPage.jsx'));
const BulkUploadPage = lazy(() => import('./pages/BulkUploadPage.jsx'));
const AuditMessageHistoryPage = lazy(() => import('./pages/AuditMessageHistoryPage.jsx'));
const AuditActivityLogsPage = lazy(() => import('./pages/AuditActivityLogsPage.jsx'));
const UserAdminPage = lazy(() => import('./pages/UserAdminPage.jsx'));
const ApiConfigPage = lazy(() => import('./pages/ApiConfigPage.jsx'));
const WebhookConfigPage = lazy(() => import('./pages/WebhookConfigPage.jsx'));
const SystemDashboardPage = lazy(() => import('./pages/SystemDashboardPage.jsx'));
const ReportsPage = lazy(() => import('./pages/ReportsPage.jsx'));
const IncidentsPage = lazy(() => import('./pages/IncidentsPage.jsx'));
const TrainingPage = lazy(() => import('./pages/TrainingPage.jsx'));
const EmailComposePage = lazy(() => import('./pages/EmailComposePage.jsx'));
const EmailTemplatesPage = lazy(() => import('./pages/EmailTemplatesPage.jsx'));
const PushNotificationsPage = lazy(() => import('./pages/PushNotificationsPage.jsx'));
const PortalContentPage = lazy(() => import('./pages/PortalContentPage.jsx'));
const DataExportPage = lazy(() => import('./pages/DataExportPage.jsx'));
const ProviderSettingsPage = lazy(() => import('./pages/ProviderSettingsPage.jsx'));

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Access & Identity — unauthenticated shell */}
            <Route element={<LoginPageLayout />}>
              <Route path="/" element={<LoginPage />} />
              <Route path="/login" element={<LoginPage />} />
            </Route>
            {/* Registration renders full-screen (its design hides the app chrome) */}
            <Route path="/register" element={<RegisterPage />} />

            {/* Authenticated app — gated behind a valid session, per-route RBAC,
                and the app shell. RoleRoute enforces authorized roles per path. */}
            <Route element={<RoleRoute />}>
              <Route element={<ContactManagementPageLayout />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/messaging-analytics-dashboard" element={<SystemDashboardPage />} />
                <Route path="/statistical-reports" element={<ReportsPage />} />
                <Route path="/sla-support-incidents" element={<IncidentsPage />} />
                <Route path="/project-training-schedules" element={<TrainingPage />} />
                <Route path="/contact-management" element={<ContactsPage />} />
                <Route path="/sender-id-request-queue" element={<SenderIdPage />} />
                <Route path="/credit-allocation" element={<CreditsPage />} />
                <Route path="/my-credit-balance" element={<MyCreditsPage />} />
                <Route path="/message-templates" element={<TemplatesPage />} />
                <Route path="/compose-sms" element={<ComposeSmsPage />} />
                <Route path="/user-administration" element={<UserAdminPage />} />
                <Route path="/my-profile" element={<ProfilePage />} />
                <Route path="/group-sms" element={<GroupSmsPage />} />
                <Route path="/bulk-messaging-upload" element={<BulkUploadPage />} />
                <Route path="/audit-log-history" element={<AuditMessageHistoryPage />} />
                <Route path="/audit-activity-logs" element={<AuditActivityLogsPage />} />
                <Route path="/api-configuration" element={<ApiConfigPage />} />
                <Route path="/webhook-configuration" element={<WebhookConfigPage />} />
                <Route path="/email-compose" element={<EmailComposePage />} />
                <Route path="/email-templates" element={<EmailTemplatesPage />} />
                <Route path="/push-notifications" element={<PushNotificationsPage />} />
                <Route path="/portal-content" element={<PortalContentPage />} />
                <Route path="/data-export" element={<DataExportPage />} />
                <Route path="/provider-settings" element={<ProviderSettingsPage />} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
